import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { h } from 'preact';

export type ToastVariant = 'neutral' | 'success' | 'error';

export interface ToastShape {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
}

interface ToastShowOpts {
  variant?: ToastVariant;
  duration?: number;
}

export interface UseToastResult {
  show: (message: string, opts?: ToastShowOpts) => void;
}

// A small event-bus so any component (or imperative code in the runtime)
// can fire a toast without prop drilling.
const listeners = new Set<(t: ToastShape) => void>();

function emit(t: ToastShape) {
  listeners.forEach((cb) => cb(t));
}

if (typeof window !== 'undefined') {
  window.addEventListener('vs:toast', (e) => {
    const detail = (e as CustomEvent).detail as ToastShowOpts & { message: string };
    if (!detail?.message) return;
    emit({
      id: cryptoUUID(),
      message: detail.message,
      variant: detail.variant ?? 'neutral',
      duration: detail.duration ?? 3000
    });
  });
}

function cryptoUUID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

export function useToast(): UseToastResult {
  const show = useCallback((message: string, opts?: ToastShowOpts) => {
    emit({
      id: cryptoUUID(),
      message,
      variant: opts?.variant ?? 'neutral',
      duration: opts?.duration ?? 3000
    });
  }, []);
  return { show };
}

const VARIANT_STYLES: Record<ToastVariant, string> = {
  neutral: 'bg-neutral-900/95 text-white dark:bg-neutral-100/95 dark:text-neutral-900',
  success: 'bg-emerald-600/95 text-white',
  error: 'bg-rose-600/95 text-white'
};

export function ToastHost() {
  const [toasts, setToasts] = useState<ToastShape[]>([]);

  useEffect(() => {
    const cb = (t: ToastShape) => {
      setToasts((cur) => [...cur, t]);
      window.setTimeout(() => {
        setToasts((cur) => cur.filter((x) => x.id !== t.id));
      }, t.duration);
    };
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);

  const containerProps = useMemo(
    () => ({
      className:
        'pointer-events-none fixed inset-x-0 bottom-[calc(var(--vs-sab)+24px)] z-[60] flex flex-col items-center gap-2 px-4'
    }),
    []
  );

  return h(
    'div',
    containerProps,
    toasts.map((t) =>
      h(
        'div',
        {
          key: t.id,
          className: `pointer-events-auto animate-slide-up rounded-full px-4 py-2 text-[13px] font-medium tracking-tight shadow-[0_8px_24px_-4px_rgba(0,0,0,0.25),0_2px_4px_rgba(0,0,0,0.1)] backdrop-blur-md ${VARIANT_STYLES[t.variant]}`,
          role: 'status'
        },
        t.message
      )
    )
  );
}
